import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SafeHtml } from '@angular/platform-browser';
import { EMPTY, interval, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { switchMap, share, takeUntil, catchError, take } from 'rxjs/operators';
import { $asyncReportClient, AsyncReportStatus } from '../../../shared/constants/async-report.constants';
import { IAsyncReportResponse } from '../../../shared/interfaces/async-report.interface';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';
import { ApiQueryOption } from '../../../shared/constants/api';
import { FileExtension } from '../../../shared/constants/file-extension';
import { IAsyncReportPollingConfig } from '../interfaces/async-report.interface';
import { NotificationPosition } from '../models/notification.model';
import { PULLING_INTERVAL } from '../shared/constants';
import { NotificationService } from './notification.service';
import { RestService } from './rest.service';
import { LoaderService } from './loader.service';

@Injectable()
export class AsyncReportService implements OnDestroy {
  private subscriptions: Subscription;
  private stopReportPolling: Subject<void>;
  private reportPollingObservable$: Observable<IAsyncReportResponse>;
  private pollingConfig: IAsyncReportPollingConfig;
  private reportUrl$: ReplaySubject<string>;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private restService: RestService,
    private notificationService: NotificationService,
    private loaderService: LoaderService,
  ) {
    this.subscriptions = new Subscription();
    this.stopReportPolling = new Subject<void>();
    this.reportUrl$ = new ReplaySubject<string>();
  }

  public init(pollingConfig: IAsyncReportPollingConfig): void {
    this.pollingConfig = pollingConfig;
    this.resetPollingSubscription();
  }

  public downloadReport(): void {
    if (SharedCommonUtility.isNullish(this.pollingConfig)) {
      throw new Error('Config is empty. Please call init().');
    }

    this.subscriptions.add(
      this.pollingConfig.getReport$.subscribe({
        next: this.handleAsyncReportGetResponse.bind(this),
        error: (error: HttpErrorResponse) => {
          console.error(`[${this.constructor.name}.downloadReport]`, error);
          this.showReportGenerationErrorToast();
          this.stopPolling();
        },
      }),
    );
  }

  public triggerAutoDownload(handleDownload: (fileExtension?: FileExtension) => void): void {
    this.subscriptions.add(
      this.activatedRoute.queryParams.pipe(take(1)).subscribe((params: Params) => {
        if (SharedCommonUtility.isNullish(params[ApiQueryOption.downloadReport])) {
          return;
        }

        const fileExtension: FileExtension =
          Object.values(FileExtension).find((_extension: FileExtension) => _extension === params[ApiQueryOption.format]) ?? null;

        this.router.navigate([], {
          replaceUrl: true,
          relativeTo: this.activatedRoute,
          queryParams: this.removeAutoDownloadParameters(params),
        });

        handleDownload(fileExtension);
      }),
    );
  }

  public downloadReportFile(): void {
    const download$: Observable<void> = this.reportUrl$.pipe(
      switchMap((url: string) => {
        return this.restService.download(url);
      }),
    );

    this.subscriptions.add(
      download$.subscribe({
        error: (error: HttpErrorResponse) => {
          console.error(`[${this.constructor.name}.downloadReportFile]`, error);
          this.showDownloadErrorToast();
          return EMPTY;
        },
      }),
    );
  }

  private removeAutoDownloadParameters(params: Params): Params {
    const queryParams: Params = { ...params };
    delete queryParams[ApiQueryOption.downloadReport];
    delete queryParams[ApiQueryOption.format];
    return queryParams;
  }

  private handleAsyncReportGetResponse(response: IAsyncReportResponse): void {
    if (SharedCommonUtility.isNullish(response) || response[$asyncReportClient.status] !== AsyncReportStatus.completed) {
      this.generateDownload();
      return;
    }

    if (response[$asyncReportClient.status] === AsyncReportStatus.completed) {
      this.downloadReportFileAndShowSuccessToast(response[$asyncReportClient.reportUrl]);
      this.reportUrl$.next(response[$asyncReportClient.reportUrl]);
    }
  }

  private generateDownload(): void {
    this.subscriptions.add(
      this.pollingConfig.postReport$.subscribe({
        next: (response: IAsyncReportResponse) => {
          this.triggerDownloadPolling();
        },
        error: (error: any) => {
          console.error(`[${this.constructor.name}.generateDownload]`, error);
          this.showReportGenerationErrorToast();
          this.stopPolling();
        },
      }),
    );
  }

  private downloadReportFileAndShowSuccessToast(url: string): void {
    this.subscriptions.add(
      this.restService.download(url).subscribe({
        next: () => this.showDownloadSuccessToast(url),
        error: (error: HttpErrorResponse) => {
          console.error(`[${this.constructor.name}.downloadReport]`, error);
          this.showDownloadErrorToast();
          return EMPTY;
        },
      }),
    );
  }

  private triggerDownloadPolling(): void {
    this.showReportInProgressToast();

    if (SharedCommonUtility.notNullish(this.reportPollingObservable$)) {
      return;
    }

    this.reportPollingObservable$ = interval(PULLING_INTERVAL.FAST).pipe(
      switchMap((): Observable<IAsyncReportResponse> => {
        this.loaderService.skipNext();
        return this.pollingConfig.getReport$;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(`[${this.constructor.name}.triggerDownloadPolling]`, error);
        this.showReportGenerationErrorToast();
        this.stopPolling();
        return EMPTY;
      }),
      share(),
      takeUntil(this.stopReportPolling),
    );
    this.subscriptions.add(
      this.reportPollingObservable$.subscribe({
        next: this.handleAsyncReportPollingResponse.bind(this),
      }),
    );
  }

  private showReportInProgressToast(): void {
    this.notificationService.warn(this.pollingConfig.messages.reportIsBeingGenerated, NotificationPosition.Toast, false);
  }

  private showReportGenerationSuccessToast(url: string): void {
    this.subscriptions.add(
      this.pollingConfig.messages.reportHasBeenGenerated(url).subscribe({
        next: (message: SafeHtml) => {
          this.notificationService.actionableSuccess(message, {
            label: 'download_report',
            onClick: this.downloadReportFile.bind(this),
          });
        },
      }),
    );
  }

  private showDownloadSuccessToast(url: string): void {
    this.subscriptions.add(
      this.pollingConfig.messages.reportIsDownloading(url).subscribe({
        next: (message: SafeHtml) => {
          this.notificationService.actionableSuccess(message, {
            label: 'click_here_download',
            onClick: this.downloadReportFile.bind(this),
          });
        },
      }),
    );
  }

  private showDownloadErrorToast(): void {
    this.subscriptions.add(
      this.pollingConfig.messages.reportDownloadError.subscribe({
        next: (message: SafeHtml) => {
          this.notificationService.error(message, NotificationPosition.Toast);
        },
      }),
    );
  }

  private showReportGenerationErrorToast(): void {
    this.subscriptions.add(
      this.pollingConfig.messages.reportGenerationError.subscribe({
        next: (message: SafeHtml) => {
          this.notificationService.error(message, NotificationPosition.Toast);
        },
      }),
    );
  }

  private handleAsyncReportPollingResponse(reportStatus: IAsyncReportResponse): void {
    switch (reportStatus?.[$asyncReportClient.status]) {
      case AsyncReportStatus.completed:
        this.stopPolling();
        this.reportUrl$.next(reportStatus[$asyncReportClient.reportUrl]);
        this.showReportGenerationSuccessToast(reportStatus[$asyncReportClient.reportUrl]);
        break;
      case AsyncReportStatus.error:
        this.stopPolling();
        this.showReportGenerationErrorToast();
        break;
      default:
        break;
    }
  }

  private stopPolling(): void {
    this.stopReportPolling.next();
    this.reportPollingObservable$ = null;
  }

  public resetPollingSubscription(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.stopReportPolling = new Subject<void>();
    this.reportPollingObservable$ = null;
    this.reportUrl$ = new ReplaySubject<string>();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
